'use client'

import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { clsx } from 'clsx'

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip: React.FC<TooltipPrimitive.TooltipProps> = ({ ...props }) => (
  <TooltipPrimitive.Root {...props} />
)
Tooltip.displayName = TooltipPrimitive.Tooltip.displayName

const TooltipTrigger = TooltipPrimitive.Trigger

type TooltipContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
>

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={clsx(
      'z-tooltip cursor-default select-none overflow-hidden max-w-[264px] rounded-md border border-dark-dim bg-dark-bright text-dark-bg py-0.5 px-2 text-xs leading-snug animate-in fade-in-50 duration-75 font-normal whitespace-normal',
      className
    )}
    {...props}
  />
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export type SimpleTooltipProps = {
  content: React.ReactNode
  delayDuration?: TooltipPrimitive.TooltipProps['delayDuration']
  disabled?: boolean
  contentClassName?: string
} & Omit<TooltipContentProps, 'className' | 'children' | 'content'>

const SimpleTooltip = ({
  children,
  content,
  sideOffset = 8,
  delayDuration,
  disabled,
  align = 'center',
  contentClassName,
  ...rest
}: SimpleTooltipProps & {
  children: React.ReactNode
}) => {
  return (
    <Tooltip delayDuration={delayDuration}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {!disabled && (
        <TooltipPrimitive.Portal>
          <TooltipContent
            {...rest}
            align={align}
            sideOffset={sideOffset}
            className={contentClassName}
          >
            {content}
          </TooltipContent>
        </TooltipPrimitive.Portal>
      )}
    </Tooltip>
  )
}

const TooltipArrow = TooltipPrimitive.Arrow

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipArrow,
  SimpleTooltip,
}
